import React, { useMemo, useState, useEffect, useRef } from 'react'
import { Table, Form, Col, Accordion, Card, Badge } from 'react-bootstrap'
import { IPublisher } from '../types'
import { IFilterSelectorOpts } from '../../home/dashboard-content/types'

const cellStyle: React.CSSProperties = {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    verticalAlign: 'top',
}

type DuplicateCampaignTableProps = {
    publishers: IPublisher[]
    filters: IFilterSelectorOpts
    initialDeselected?: string[]
    onChange?: (excluded: string[]) => void
}

    // const normalizeShopUrl = (p: IPublisher): string => {
    //     if (!p.shopUrl) return p.id
    //     return p.shopUrl.trim().replace(/\/+$/, '')
    // }
    const normalizeShopUrl = (p: IPublisher): string => {
    if (!p.shopUrl) return p.id

    let raw = p.shopUrl.trim().toLowerCase()

    if (!raw.startsWith('http')) raw = `https://${raw}`

    try {
        const url = new URL(raw)

        const hostname = url.hostname.replace(/^www\./, '')
        const pathname = url.pathname.replace(/\/$/, '')

        return `${hostname}${pathname}`
    } catch {
        return raw
            .replace(/^www\./, '')
            .replace(/\/$/, '')
    }
}


const DuplicateCampaignTable: React.FC<DuplicateCampaignTableProps> = ({
    publishers,
    filters,
    initialDeselected,
    onChange,
}) => {
    const [excludedItems, setExcludedItems] = useState<Record<string, boolean>>({})
    const onChangeRef = useRef(onChange)

    useEffect(() => {
        onChangeRef.current = onChange
    }, [onChange])

    const filteredDuplicates = useMemo(() => {
        const { campaigns, channels, countries, networks } = filters

        const matchesFilter = (p: IPublisher) =>
            networks.includes(p.network) &&
            countries.includes(p.country) &&
            campaigns.some(c => c.id === p.id) &&
            channels.some(ch => ch.id === p.publisherId)

        const filtered = publishers.filter(matchesFilter)

        const grouped: Record<string, IPublisher[]> = {}
        filtered.forEach(p => {
            const key = normalizeShopUrl(p)
            if (!grouped[key]) grouped[key] = []
            grouped[key].push(p)
        })

        return Object.values(grouped)
            .filter(group => group.length > 1)
            .flat()
    }, [publishers, filters])

    const groupedDuplicates = useMemo(() => {
        const grouped: Record<string, IPublisher[]> = {}

        filteredDuplicates.forEach(p => {
            const key = normalizeShopUrl(p)
            if (!grouped[key]) grouped[key] = []
            grouped[key].push(p)
        })

        return grouped
    }, [filteredDuplicates])

    useEffect(() => {
        setExcludedItems(prev => {
            let changed = false
            const next = { ...prev }

            filteredDuplicates.forEach(p => {
                const key = `${p.id}-${p.publisherId}`
                if (!(key in next)) {
                    next[key] = initialDeselected?.includes(key) ?? false
                    changed = true
                }
            })

            Object.keys(next).forEach(key => {
                if (!filteredDuplicates.some(p => `${p.id}-${p.publisherId}` === key)) {
                    delete next[key]
                    changed = true
                }
            })

            return changed ? next : prev
        })
    }, [filteredDuplicates, initialDeselected])

    const excluded = useMemo(
        () =>
            Object.entries(excludedItems)
                .filter(([_, excluded]) => excluded)
                .map(([key]) => key),
        [excludedItems]
    )

    useEffect(() => {
        onChangeRef.current?.(excluded)
    }, [excluded])

    const handleExcludeToggle = (publisher: IPublisher) => {
        const key = `${publisher.id}-${publisher.publisherId}`
        setExcludedItems(prev => ({ ...prev, [key]: !prev[key] }))
    }

    const duplicateGroupCount = useMemo(() => {
        const grouped: Record<string, number> = {}

        filteredDuplicates.forEach(p => {
            const rowKey = `${p.id}-${p.publisherId}`
            if (excludedItems[rowKey]) return

            const groupKey = normalizeShopUrl(p)
            grouped[groupKey] = (grouped[groupKey] || 0) + 1
        })

        return Object.values(grouped).filter(count => count > 1).length
    }, [filteredDuplicates, excludedItems])

    const totalSelectedCampaigns = useMemo(() => {
        const { campaigns, channels, countries, networks } = filters

        const filtered = publishers.filter(p =>
            networks.includes(p.network) &&
            countries.includes(p.country) &&
            campaigns.some(c => c.id === p.id) &&
            channels.some(ch => ch.id === p.publisherId)
        )

        const grouped: Record<string, IPublisher[]> = {}
        filtered.forEach(p => {
            const key = normalizeShopUrl(p)
            if (!grouped[key]) grouped[key] = []
            grouped[key].push(p)
        })

        return Object.values(grouped).filter(group =>
            group.some(p => {
                const rowKey = `${p.id}-${p.publisherId}`
                return !excludedItems[rowKey]
            })
        ).length
    }, [publishers, filters, excludedItems])

    return (
        <Col md={12} style={{ minWidth: 0, overflowX: 'hidden' }}>
            <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontWeight: 600 }}>
                    {totalSelectedCampaigns} campaigns will be used
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                    {duplicateGroupCount > 0 ? (
                        <>
                            {duplicateGroupCount} duplicate campaign groups detected.
                            <br />
                            For duplicates, the campaign with the highest commission is selected by default.
                        </>
                    ) : (
                        'No duplicate campaigns detected'
                    )}
                </div>
            </div>

            <Accordion>
                <Accordion.Item eventKey="duplicates">
                    <Accordion.Header>
                        <span style={{ marginRight: '0.5rem' }}>
                            Duplicate campaigns
                        </span>
                        {duplicateGroupCount > 0 ? (
                            <Badge bg="danger">{duplicateGroupCount}</Badge>
                        ) : (
                            <Badge bg="success">Resolved</Badge>
                        )}
                    </Accordion.Header>

                    <Accordion.Body style={{ overflowX: 'hidden' }}>
                        <p>
                            Duplicate campaigns typically occur when the same campaign
                            is approved on multiple channels or exists across multiple
                            networks.
                            <br />
                            Exclude duplicates below to control which campaigns are used.
                        </p>

                        <Card className="shadow-sm overflow-hidden">
                            <Card.Body className="p-0" style={{ maxWidth: '100%', overflowX: 'hidden' }}>
                                <div style={{ maxWidth: '100%', height: '300px', overflowX: 'auto', overflowY: 'auto' }}>
                                    <Table hover size="sm" className="mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                        <thead style={{ backgroundColor: '#5b9bd5', color: '#ffffff' }}>
                                            <tr>
                                                <th style={{ width: '28%', color: '#ffffff' }}>Campaign</th>
                                                <th style={{ width: '14%', color: '#ffffff' }}>Network</th>
                                                <th style={{ width: '10%', color: '#ffffff' }}>Country</th>
                                                <th style={{ width: '24%', color: '#ffffff' }}>Channel</th>
                                                <th style={{ width: '14%', color: '#ffffff' }}>Commission</th>
                                                <th style={{ width: '10%', color: '#ffffff' }}>Exclude</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(groupedDuplicates).length > 0 ? (
                                                Object.entries(groupedDuplicates).map(
                                                    ([groupKey, publishers]) => (
                                                        <React.Fragment key={groupKey}>
                                                            <tr
                                                                style={{
                                                                    backgroundColor: '#e9ecef',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                <td colSpan={6} style={{ ...cellStyle, overflowWrap: 'anywhere' }}>
                                                                    {groupKey}
                                                                </td>
                                                            </tr>

                                                            {publishers.map(p => {
                                                                const key = `${p.id}-${p.publisherId}`
                                                                const isExcluded = !!excludedItems[key]

                                                                return (
                                                                    <tr
                                                                        key={key}
                                                                        style={{
                                                                            opacity: isExcluded ? 0.5 : 1,
                                                                            backgroundColor: isExcluded
                                                                                ? '#f8f9fa'
                                                                                : '#fff',
                                                                        }}
                                                                    >
                                                                        <td style={cellStyle}>{p.name}</td>
                                                                        <td style={cellStyle}>{p.network}</td>
                                                                        <td style={cellStyle}>{p.country}</td>
                                                                        <td style={cellStyle}>{p.publisherName}</td>
                                                                        <td style={cellStyle}>
                                                                            {p.commission
                                                                                ? `${p.commission}%`
                                                                                : ''}
                                                                        </td>
                                                                        <td style={cellStyle}>
                                                                            <Form.Check
                                                                                checked={isExcluded}
                                                                                onChange={() =>
                                                                                    handleExcludeToggle(p)
                                                                                }
                                                                            />
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            })}
                                                        </React.Fragment>
                                                    )
                                                )
                                            ) : (
                                                <tr>
                                                    <td
                                                        colSpan={6}
                                                        className="text-center text-muted p-3"
                                                    >
                                                        No duplicate campaigns to review
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </Col>
    )
}

export default DuplicateCampaignTable
